/**
 * The two things a file hit says that nothing else in this shell has had to
 * render: how big it is and what it is.
 *
 * Local to search rather than in `components/format.ts` because search is
 * currently the only surface that shows a blob's size — §9.1 indexes files on
 * "filename, uploader, and type", so this is the screen where those three have
 * to read as a sentence. If the file node page grows the same line, both should
 * move up to `components/format.ts` rather than being written twice.
 */

const UNITS = ['bytes', 'KB', 'MB', 'GB', 'TB'] as const;

/**
 * Binary multiples with the short labels, which is what every developer in this
 * building reads a file size in. The exactness matters less than the agreement:
 * the number beside a filename should be the number their operating system
 * showed them when they picked it.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} bytes`;

  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal below 10, none above: "1.4 MB" is informative, "14.3 MB" is noise.
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${UNITS[unit]}`;
}

/**
 * A MIME type in the words people use for it.
 *
 * The fallback is the raw type rather than "file" or "unknown": the uploader
 * chose it, and a person looking at a search result is better served by
 * `application/vnd.figma` than by a shrug. Only the types Matchbox actually
 * moves are named here — §4.5's "zips, PDFs, images, source files" — and the
 * list is not worth growing speculatively.
 */
const CONTENT_TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/zip': 'ZIP archive',
  'application/x-zip-compressed': 'ZIP archive',
  'application/json': 'JSON',
  'image/png': 'PNG image',
  'image/jpeg': 'JPEG image',
  'image/gif': 'GIF image',
  'image/svg+xml': 'SVG image',
  'image/webp': 'WebP image',
  'text/csv': 'CSV',
  'text/markdown': 'Markdown',
  'text/plain': 'Text',
};

export function contentTypeLabel(contentType: string): string {
  if (contentType === '') return 'unknown type';
  return CONTENT_TYPE_LABELS[contentType] ?? contentType;
}
